'use client'

import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Calendar, Upload, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface LeaveRequestFormProps {
  staffId: string
  clinicId: string
  onSuccess?: () => void
}

export default function LeaveRequestForm({
  staffId,
  clinicId,
  onSuccess
}: LeaveRequestFormProps) {
  const { t } = useTranslation('hr')
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    absenceType: '',
    startDate: '',
    endDate: '',
    reason: '',
    halfDay: false,
    supportingDocument: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await fetch('/api/hr/absences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staffId,
          clinicId,
          absenceType: formData.absenceType,
          startDate: formData.startDate,
          endDate: formData.endDate,
          reason: formData.reason,
          halfDay: formData.halfDay,
          supportingDocument: formData.supportingDocument
        })
      })

      const data = await response.json()

      if (data.success) {
        alert(t('leave.submitSuccess'))
        // Reset form
        setFormData({
          absenceType: '',
          startDate: '',
          endDate: '',
          reason: '',
          halfDay: false,
          supportingDocument: ''
        })
        onSuccess?.()
      } else {
        alert(t('leave.submitError') + ': ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Submit error:', error)
      alert(t('leave.submitErrorRetry'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('leave.requestTimeOff')}</CardTitle>
        <CardDescription>
          {t('leave.requestDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Absence Type */}
          <div className="space-y-2">
            <Label htmlFor="absenceType">{t('leave.leaveType')}</Label>
            <Select
              value={formData.absenceType}
              onValueChange={(value) => setFormData({ ...formData, absenceType: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder={t('leave.selectLeaveType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vacation">{t('leave.vacation')}</SelectItem>
                <SelectItem value="sick_leave">{t('leave.sickLeave')}</SelectItem>
                <SelectItem value="parental_leave">{t('leave.parentalLeave')}</SelectItem>
                <SelectItem value="unpaid_leave">{t('leave.unpaidLeave')}</SelectItem>
                <SelectItem value="other">{t('leave.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">{t('leave.startDate')}</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">{t('leave.endDate')}</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                min={formData.startDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Half Day Option */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="halfDay"
              checked={formData.halfDay}
              onChange={(e) => setFormData({ ...formData, halfDay: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="halfDay" className="font-normal cursor-pointer">
              {t('leave.halfDay')}
            </Label>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">{t('leave.reason')}</Label>
            <Textarea
              id="reason"
              placeholder={t('leave.reasonPlaceholder')}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
            />
          </div>

          {/* Supporting Document */}
          <div className="space-y-2">
            <Label htmlFor="document">{t('leave.supportingDocument')}</Label>
            <div className="flex gap-2">
              <Input
                id="document"
                type="url"
                placeholder={t('leave.documentPlaceholder')}
                value={formData.supportingDocument}
                onChange={(e) => setFormData({ ...formData, supportingDocument: e.target.value })}
              />
              <Button type="button" variant="outline" size="icon">
                <Upload className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('leave.sickLeaveNote')}
            </p>
          </div>

          {/* Submit Button */}
          <Button type="submit" disabled={loading} className="w-full">
            <Send className="h-4 w-4 mr-2" />
            {loading ? t('leave.submitting') : t('leave.submitRequest')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

